import { projectHostingTypeSchema } from "schemas/projectHostingType";
import { workspaceDefaultProjectTypeSchema } from "schemas/workspaceDefaultProjectType";
import * as z from "zod";

/**
 * Convert a user's name to a single string if possible, returning null if not. This is to avoid
 * using the string templating everywhere, and ending up with blank names when they don't have
 * either set.
 *
 * NOTE Ben 2024-05-09: I added this here, because it seems like we do something like this across
 * our packages, and while this isn't a schema, it's the place where we put our model-like code.
 */
export function userNameNullable(user: {
  firstName: string | undefined | null;
  lastName: string | undefined | null;
}): string | null {
  if (user.firstName) {
    if (user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName;
  }
  return null;
}

export const userSchema = z
  .object({
    id: z.uuid(),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    name: z.string().nullish(),
    heardFrom: z.array(z.string()),
    profileType: z.string().nullable(),
    registeredAt: z.date().nullable(),
    verifiedAt: z.date().nullable(),
    isSuperuser: z.boolean(),
    referrerKey: z.string().nullable(),
    source: z.enum(["shopify", "website", "artifact_comment"]).nullable(),
    authType: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    lastLogin: z.date().nullable(),
    referralCodeId: z.string().nullable(),
    deletedAt: z.date().nullable(),
    workspace: z
      .object({
        id: z.uuid(),
        isOnboarded: z.boolean(),
        isApproved: z.boolean(),
        defaultProjectType: workspaceDefaultProjectTypeSchema,
      })
      .nullish(),
    referralCode: z
      .object({
        id: z.uuid(),
        code: z.string(),
      })
      .nullish(),
    industry: z.string().nullable(),
    annualRevenue: z.number().nullable(),
    clientsAnnualRevenue: z.number().nullable(),
    clientCount: z.number().nullable(),
    defaultProjectId: z.string().nullish(),
    defaultProjectHostingType: projectHostingTypeSchema.nullish(),
    defaultProjectAgentEnabledAt: z.date().nullish(),
    // The default pick restricted to agent-enabled projects; see getDefaultProjects.
    defaultAgentProjectId: z.string().nullish(),
    profileImageUrl: z.string().nullable(),
    agentSidebarNavigationEnabled: z.boolean().optional(),
    jwt: z.string().nullish(),
  })
  .describe("User");

export const userRoleSchema = z
  .enum([
    "superuser",
    "workspaceOwner",
    "workspaceMember",
    "workspaceViewer",
    "projectMember",
  ])
  .describe("UserRole");
